import { useEffect, useMemo, useRef, useState } from "react";

import type { StockRuntimeItem } from "../runtime/gateway/local-projection-client";
import type {
  StockTransferDestination,
  StockTransferRuntimeProjection,
  StockTransferSnapshot,
} from "../runtime/gateway/stock-transfer-client";
import { commitStockTransferDispatch, commitStockTransferReceive } from "../runtime/local-store/stock-transfer-state";
import { readLocalStockMovementHistory } from "../runtime/local-store/stock-movement-runtime";
import type { TrustedTerminalIdentity } from "../runtime/local-store/terminal-state";
import "./StockInternalUseBatch.css";
import "./StockTransferWorkspace.css";

type DraftLine = {
  item: StockRuntimeItem;
  quantity: string;
  unitName: string;
};

type ReceiveDraftLine = {
  stockItemId: string;
  name: string;
  dispatched: number;
  received: string;
  unitName: string;
};

type Props = {
  identity: TrustedTerminalIdentity;
  deviceSecret: string;
  sessionToken: string;
  items: StockRuntimeItem[];
  runtime: StockTransferRuntimeProjection;
  seedItem?: StockRuntimeItem | null;
  online: boolean;
  initialMode?: "SEND" | "RECEIVE";
  onClose: () => void;
};

function requestKey(kind: "dispatch" | "receive") {
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pos-stock-transfer-${kind}:${id}`;
}

function quantityLabel(value: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return new Intl.NumberFormat("th-TH", { maximumFractionDigits: 3 }).format(Number(value));
}

function normalizedSearch(value: string) {
  return value.trim().toLocaleLowerCase("th-TH");
}

function defaultQuantity(_item: StockRuntimeItem) {
  return "1";
}

function sendLineError(line: DraftLine) {
  if (!line.quantity.trim()) return "ใส่จำนวน";
  const quantity = Number(line.quantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return "จำนวนต้องมากกว่า 0";
  return null;
}

function transferError(value: string) {
  if (value.includes("STOCK_TRANSFER_ONLINE_REQUIRED") || value.includes("GATEWAY_UNREACHABLE") || value.includes("GATEWAY_TIMEOUT")) {
    return "ตอนนี้ยังยืนยันการโอนไม่ได้ เพราะ Transfer v1 ต้องเชื่อมต่อ Stock Core ตอน Commit";
  }
  if (value.includes("STOCK_TRANSFER_SEND_PERMISSION_REQUIRED")) return "คุณไม่มีสิทธิ์ส่งโอนสต็อกจากสาขานี้";
  if (value.includes("STOCK_TRANSFER_RECEIVE_PERMISSION_REQUIRED")) return "คุณไม่มีสิทธิ์รับโอนสต็อกเข้าสาขานี้";
  if (value.includes("STOCK_TRANSFER_DESTINATION_NOT_ELIGIBLE") || value.includes("STOCK_TRANSFER_DESTINATION_MUST_DIFFER")) return "สาขาปลายทางนี้ไม่พร้อมรับการโอนตามกฎของ Stock Core";
  if (value.includes("STOCK_TRANSFER_DESTINATION_ITEM_NOT_ELIGIBLE")) return "มีสินค้าบางรายการที่ปลายทางยังไม่พร้อมรับ กรุณาตรวจการตั้งค่าสต็อกของสาขาปลายทาง";
  if (value.includes("STOCK_TRANSFER_COST_ALLOCATION_POLICY_REQUIRED")) return "รายการนี้มี Cost Layer ที่ยังไม่มี Published Transfer Allocation Policy จึงยังส่งโอนไม่ได้";
  if (value.includes("INSUFFICIENT_STOCK_FOR_ITEM") || value.includes("NEGATIVE_STOCK_NOT_ALLOWED")) return "Stock Core ยังไม่อนุญาตรายการนี้ตามนโยบายปัจจุบัน กรุณารีเฟรชและตรวจอีกครั้ง";
  if (value.includes("STOCK_TRANSFER_REQUEST_KEY_CONFLICT")) return "รายละเอียดการโอนเปลี่ยนหลังเริ่มยืนยัน กรุณาตรวจรายการแล้วส่งใหม่";
  if (value.includes("STOCK_TRANSFER_NOT_RECEIVABLE") || value.includes("STOCK_TRANSFER_DESTINATION_SCOPE_MISMATCH")) return "รายการนี้ไม่อยู่ในสถานะที่สาขาปัจจุบันรับได้แล้ว";
  if (value.includes("STOCK_TRANSFER_RECEIVED_QUANTITY_EXCEEDS_DISPATCH")) return "จำนวนรับจริงห้ามมากกว่าจำนวนที่สาขาต้นทางส่งมา";
  if (value.includes("STOCK_TRANSFER_VARIANCE_NOTE_REQUIRED")) return "กรุณาระบุสาเหตุเมื่อจำนวนรับจริงไม่ครบ";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณาเข้าสู่ระบบพนักงานใหม่";
  return "ทำรายการโอนสต็อกไม่สำเร็จ กรุณาตรวจรายการแล้วลองใหม่";
}

function transferTime(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function StockTransferWorkspace({
  identity,
  deviceSecret,
  sessionToken,
  items,
  runtime,
  seedItem = null,
  online,
  initialMode,
  onClose,
}: Props) {
  const fallbackMode = runtime.can_send ? "SEND" : "RECEIVE";
  const [mode, setMode] = useState<"SEND" | "RECEIVE">(initialMode ?? fallbackMode);
  const [destinationId, setDestinationId] = useState("");
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [query, setQuery] = useState("");
  const [note, setNote] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doneMessage, setDoneMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [activeRequestKey, setActiveRequestKey] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [receiveTransfer, setReceiveTransfer] = useState<StockTransferSnapshot | null>(null);
  const [receiveDraft, setReceiveDraft] = useState<ReceiveDraftLine[]>([]);
  const backdropRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const seededDestinationRef = useRef<string>("");

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const syncViewport = () => {
      const backdrop = backdropRef.current;
      if (!backdrop) return;
      backdrop.style.setProperty("--pos-stock-visible-height", `${Math.max(1, viewport.height)}px`);
      backdrop.style.setProperty("--pos-stock-visible-offset", `${Math.max(0, viewport.offsetTop)}px`);
    };
    syncViewport();
    viewport.addEventListener("resize", syncViewport);
    viewport.addEventListener("scroll", syncViewport);
    return () => {
      viewport.removeEventListener("resize", syncViewport);
      viewport.removeEventListener("scroll", syncViewport);
    };
  }, []);

  const destinations = runtime.can_send ? runtime.eligible_destinations : [];
  const destination = destinations.find((entry) => entry.branch_id === destinationId) ?? null;
  const eligibleIds = useMemo(() => new Set(destination?.eligible_stock_item_ids ?? []), [destination]);
  const addedIds = useMemo(() => new Set(draft.map((line) => line.item.stock_item_id)), [draft]);
  const searchText = normalizedSearch(query);
  const candidates = useMemo(() => items
    .filter((item) => item.active && eligibleIds.has(item.stock_item_id))
    .filter((item) => {
      if (!searchText) return true;
      return [item.canonical_name, item.item_code, item.sku_code ?? "", ...(item.barcodes ?? [])]
        .join(" ")
        .toLocaleLowerCase("th-TH")
        .includes(searchText);
    })
    .sort((left, right) => left.canonical_name.localeCompare(right.canonical_name, "th"))
    .slice(0, 120), [eligibleIds, items, searchText]);

  const sendErrors = useMemo(() => new Map(draft.map((line) => [line.item.stock_item_id, sendLineError(line)])), [draft]);
  const sendInvalidCount = [...sendErrors.values()].filter(Boolean).length;
  const canReviewSend = Boolean(destination && draft.length && sendInvalidCount === 0);

  const inbound = useMemo(() => runtime.items
    .filter((item) => item.direction === "INCOMING" && item.status === "IN_TRANSIT" && item.next_action === "RECEIVE")
    .sort((left, right) => new Date(right.dispatched_at).getTime() - new Date(left.dispatched_at).getTime()), [runtime.items]);
  const reviewRequired = useMemo(() => runtime.items
    .filter((item) => item.status === "VARIANCE_REVIEW_REQUIRED")
    .sort((left, right) => new Date(right.changed_at).getTime() - new Date(left.changed_at).getTime()), [runtime.items]);

  const receiveVariance = receiveDraft.some((line) => {
    const received = Number(line.received);
    return Number.isFinite(received) && received >= 0 && received < line.dispatched;
  });
  const receiveInvalidCount = receiveDraft.filter((line) => {
    if (!line.received.trim()) return true;
    const received = Number(line.received);
    return !Number.isFinite(received) || received < 0 || received > line.dispatched;
  }).length;
  const canReviewReceive = Boolean(receiveTransfer && receiveDraft.length && receiveInvalidCount === 0 && (!receiveVariance || note.trim()));

  useEffect(() => {
    if (!seedItem || !destination || seededDestinationRef.current === destination.branch_id) return;
    seededDestinationRef.current = destination.branch_id;
    if (!eligibleIds.has(seedItem.stock_item_id)) return;
    void addItem(seedItem);
  }, [destinationId]);

  function changed() {
    setActiveRequestKey("");
    setReviewing(false);
    setError(null);
    setDoneMessage("");
  }

  function dismissSearchKeyboard() {
    searchInputRef.current?.blur();
    setSearchActive(false);
  }

  async function resolveUnitName(item: StockRuntimeItem) {
    try {
      const history = await readLocalStockMovementHistory(identity, item.stock_item_id);
      return history[0]?.unit_name || "หน่วยสต็อก";
    } catch {
      return "หน่วยสต็อก";
    }
  }

  async function addItem(item: StockRuntimeItem) {
    dismissSearchKeyboard();
    if (!destination || !eligibleIds.has(item.stock_item_id)) return;
    if (addedIds.has(item.stock_item_id)) {
      setError("สินค้านี้อยู่ในชุดแล้ว แก้จำนวนที่รายการเดิมได้เลย");
      return;
    }
    const unitName = await resolveUnitName(item);
    setDraft((current) => [...current, { item, quantity: defaultQuantity(item), unitName }]);
    setQuery("");
    changed();
  }

  function changeQuantity(stockItemId: string, value: string) {
    setDraft((current) => current.map((line) => line.item.stock_item_id === stockItemId ? { ...line, quantity: value } : line));
    changed();
  }

  function stepQuantity(stockItemId: string, delta: -1 | 1) {
    const line = draft.find((candidate) => candidate.item.stock_item_id === stockItemId);
    if (!line) return;
    const quantity = Number(line.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    const next = quantity + delta;
    if (next <= 0) return;
    changeQuantity(stockItemId, String(next));
  }

  function removeItem(stockItemId: string) {
    setDraft((current) => current.filter((line) => line.item.stock_item_id !== stockItemId));
    changed();
  }

  function chooseDestination(value: string) {
    if (busy) return;
    setDestinationId(value);
    setDraft([]);
    setQuery("");
    setNote("");
    seededDestinationRef.current = "";
    changed();
  }

  function openReceive(transfer: StockTransferSnapshot) {
    setReceiveTransfer(transfer);
    setReceiveDraft(transfer.lines.map((line) => ({
      stockItemId: line.stock_item_id,
      name: line.canonical_name,
      dispatched: Number(line.dispatched_quantity),
      received: String(Number(line.dispatched_quantity)),
      unitName: line.unit_name,
    })));
    setNote("");
    setReviewing(false);
    setActiveRequestKey("");
    setError(null);
    setDoneMessage("");
  }

  function changeReceiveQuantity(stockItemId: string, value: string) {
    setReceiveDraft((current) => current.map((line) => line.stockItemId === stockItemId ? { ...line, received: value } : line));
    changed();
  }

  async function submitDispatch() {
    if (!canReviewSend || busy || !online || !destination) return;
    const key = activeRequestKey || requestKey("dispatch");
    if (!activeRequestKey) setActiveRequestKey(key);
    setBusy(true);
    setError(null);
    try {
      const result = await commitStockTransferDispatch({
        identity,
        deviceSecret,
        sessionToken,
        destinationBranchId: destination.branch_id,
        lines: draft.map((line) => ({ stockItemId: line.item.stock_item_id, quantity: Number(line.quantity) })),
        note: note.trim() || null,
        requestKey: key,
      });
      setDoneMessage(result.readbackOk
        ? `ส่งโอนไป ${destination.branch_name} ${draft.length.toLocaleString("th-TH")} รายการแล้ว · อยู่ระหว่างทาง`
        : `Stock Core รับการส่งโอนแล้ว · กำลังซิงค์สถานะล่าสุด`);
    } catch (cause) {
      setError(transferError(cause instanceof Error ? cause.message : "POS_STOCK_TRANSFER_DISPATCH_FAILED"));
    } finally {
      setBusy(false);
    }
  }

  async function submitReceive() {
    if (!canReviewReceive || busy || !online || !receiveTransfer) return;
    const key = activeRequestKey || requestKey("receive");
    if (!activeRequestKey) setActiveRequestKey(key);
    setBusy(true);
    setError(null);
    try {
      const result = await commitStockTransferReceive({
        identity,
        deviceSecret,
        sessionToken,
        transferId: receiveTransfer.transfer_id,
        lines: receiveDraft.map((line) => ({ stockItemId: line.stockItemId, receivedQuantity: Number(line.received) })),
        note: note.trim() || null,
        requestKey: key,
      });
      setDoneMessage(result.data.status === "VARIANCE_REVIEW_REQUIRED"
        ? "รับเข้าตามจำนวนจริงแล้ว · พบส่วนต่างและส่งเข้า Review โดยไม่สร้างของเสียอัตโนมัติ"
        : result.readbackOk
          ? "รับโอนครบแล้ว · Stock Core อัปเดตยอดปลายทางเรียบร้อย"
          : "Stock Core รับรายการแล้ว · กำลังซิงค์ยอดล่าสุด");
    } catch (cause) {
      setError(transferError(cause instanceof Error ? cause.message : "POS_STOCK_TRANSFER_RECEIVE_FAILED"));
    } finally {
      setBusy(false);
    }
  }

  const sendView = mode === "SEND" && runtime.can_send;

  return (
    <div ref={backdropRef} className="pos-stock-batch-backdrop" role="presentation">
      <section className="pos-stock-batch pos-stock-transfer" role="dialog" aria-modal="true" aria-labelledby="pos-stock-transfer-title" data-search-active={searchActive ? "true" : "false"}>
        <header className="pos-stock-batch__header">
          <div>
            <span>MORE Stock · {identity.branch_name}</span>
            <h2 id="pos-stock-transfer-title">โอนสาขา</h2>
            <p>จัดรายการจากข้อมูลในเครื่อง · ยืนยันกับ Stock Core ตอนส่งหรือรับเท่านั้น</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="ปิด">×</button>
        </header>

        <nav className="pos-stock-transfer__tabs" aria-label="งานโอนสาขา">
          {runtime.can_send ? <button type="button" data-active={mode === "SEND"} onClick={() => { setMode("SEND"); setReviewing(false); setReceiveTransfer(null); setError(null); }}>ส่งออก</button> : null}
          {runtime.can_receive ? <button type="button" data-active={mode === "RECEIVE"} onClick={() => { setMode("RECEIVE"); setReviewing(false); setError(null); }}>รับเข้า{inbound.length ? ` · ${inbound.length}` : ""}</button> : null}
        </nav>

        {doneMessage ? (
          <main className="pos-stock-batch__done">
            <div aria-hidden="true">✓</div>
            <h3>{doneMessage}</h3>
            <p>ประวัติ Stock และ Transfer จะยึด Owner Read-back เดียวกัน</p>
            <button type="button" onClick={onClose}>เสร็จสิ้น</button>
          </main>
        ) : sendView ? (
          <>
            {!online ? <div className="pos-stock-batch__offline">ออฟไลน์ · เลือกสาขา จัดสินค้า และตรวจรายการได้ แต่ยังยืนยันส่งไม่ได้</div> : null}
            <main className="pos-stock-batch__body" data-reviewing={reviewing ? "true" : "false"}>
              {!reviewing ? (
                <section className="pos-stock-batch__picker" aria-label="เลือกสินค้า">
                  <label className="pos-stock-transfer__destination">
                    <span>สาขาปลายทาง</span>
                    <select value={destinationId} onChange={(event) => chooseDestination(event.target.value)} disabled={busy}>
                      <option value="">เลือกสาขาปลายทาง</option>
                      {destinations.map((entry: StockTransferDestination) => <option key={entry.branch_id} value={entry.branch_id}>{entry.branch_name}</option>)}
                    </select>
                  </label>
                  {destination ? (
                    <>
                      <div className="pos-stock-batch__search">
                        <div className="pos-stock-batch__search-heading">
                          <label htmlFor="pos-stock-transfer-search">ค้นหา / สแกนสินค้า</label>
                          {searchActive ? <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={dismissSearchKeyboard}>ซ่อนแป้น</button> : null}
                        </div>
                        <input
                          ref={searchInputRef}
                          id="pos-stock-transfer-search"
                          value={query}
                          onChange={(event) => { setQuery(event.target.value); setError(null); }}
                          onFocus={() => setSearchActive(true)}
                          onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); dismissSearchKeyboard(); } }}
                          placeholder="ชื่อสินค้า SKU หรือบาร์โค้ด"
                          autoComplete="off"
                          enterKeyHint="search"
                        />
                      </div>
                      <div className="pos-stock-batch__catalog">
                        {candidates.map((item) => {
                          const onHand = item.quantity_on_hand == null ? null : Number(item.quantity_on_hand);
                          const added = addedIds.has(item.stock_item_id);
                          return (
                            <button type="button" key={item.stock_item_id} className={added ? "is-added" : ""} onClick={() => void addItem(item)}>
                              <span><strong>{item.canonical_name}</strong><small>{item.sku_code ? `SKU ${item.sku_code}` : item.item_code}</small></span>
                              <span className="pos-stock-batch__catalog-balance"><small>คงเหลือ</small><strong>{quantityLabel(onHand)}</strong>{added ? <em>อยู่ในชุดแล้ว</em> : null}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : <div className="pos-stock-transfer__choose">เลือกสาขาปลายทางก่อน ระบบจะแสดงเฉพาะสินค้าที่ Owner ระบุว่าปลายทางรับได้</div>}
                </section>
              ) : (
                <section className="pos-stock-batch__review-intro">
                  <span>ตรวจสอบก่อนส่ง</span>
                  <strong>{draft.length.toLocaleString("th-TH")} รายการ → {destination?.branch_name}</strong>
                  <p>ยอดต้นทางจะลดเมื่อ Stock Core ยืนยัน Dispatch และปลายทางจะยังไม่เพิ่มจนกว่าจะรับจริง</p>
                </section>
              )}

              <section className="pos-stock-batch__draft" aria-label="รายการส่งโอน">
                <div className="pos-stock-batch__draft-heading">
                  <div><span>รายการที่จะส่ง</span><strong>{draft.length.toLocaleString("th-TH")}</strong></div>
                  {sendInvalidCount ? <em>{sendInvalidCount.toLocaleString("th-TH")} รายการต้องตรวจ</em> : <em className="is-ready">พร้อมตรวจ</em>}
                </div>
                {!draft.length ? <div className="pos-stock-batch__empty"><strong>ยังไม่มีสินค้าในชุด</strong><span>เลือกปลายทางแล้วแตะสินค้าที่จะส่ง</span></div> : (
                  <div className="pos-stock-batch__lines">
                    {draft.map((line, index) => {
                      const itemError = sendErrors.get(line.item.stock_item_id);
                      const quantity = Number(line.quantity);
                      const onHand = line.item.quantity_on_hand == null ? null : Number(line.item.quantity_on_hand);
                      const ready = Number.isFinite(quantity) && quantity > 0;
                      const canDecrease = ready && quantity - 1 > 0;
                      const canIncrease = ready && Number.isFinite(quantity + 1);
                      const inputId = `pos-stock-transfer-qty-${line.item.stock_item_id}`;
                      return (
                        <article key={line.item.stock_item_id}>
                          <div className="pos-stock-batch__line-no">{index + 1}</div>
                          <div className="pos-stock-batch__line-name"><strong>{line.item.canonical_name}</strong><span>คงเหลือ {quantityLabel(onHand)} {line.unitName}</span></div>
                          <div className={`pos-stock-batch__quantity${itemError ? " has-error" : ""}`}>
                            <label htmlFor={inputId}>จำนวนส่ง</label>
                            <div className="pos-stock-batch__quantity-controls">
                              <button type="button" onClick={() => stepQuantity(line.item.stock_item_id, -1)} disabled={reviewing || busy || !canDecrease}>−</button>
                              <input id={inputId} type="number" min="0" step="any" inputMode="decimal" value={line.quantity} onFocus={(event) => event.currentTarget.select()} onChange={(event) => changeQuantity(line.item.stock_item_id, event.target.value)} disabled={reviewing || busy} aria-invalid={Boolean(itemError)} />
                              <button type="button" onClick={() => stepQuantity(line.item.stock_item_id, 1)} disabled={reviewing || busy || !canIncrease}>+</button>
                              <strong>{line.unitName}</strong>
                            </div>
                            {itemError ? <small>{itemError}</small> : null}
                          </div>
                          {!reviewing ? <button type="button" className="pos-stock-batch__remove" onClick={() => removeItem(line.item.stock_item_id)} aria-label={`ลบ ${line.item.canonical_name}`}>×</button> : null}
                        </article>
                      );
                    })}
                  </div>
                )}
                <label className="pos-stock-batch__note"><span>รายละเอียด (ถ้ามี)</span><textarea value={note} onChange={(event) => { setNote(event.target.value); changed(); }} rows={2} maxLength={2000} placeholder="เช่น ส่งเติมสต็อกหน้าร้าน" disabled={reviewing || busy} /></label>
                {error ? <div className="pos-stock-batch__error">{error}</div> : null}
              </section>
            </main>
            <footer className="pos-stock-batch__footer">
              {reviewing ? (
                <><button type="button" className="secondary" onClick={() => { setReviewing(false); setError(null); }} disabled={busy}>กลับไปแก้</button><div className="pos-stock-batch__footer-summary"><strong>{draft.length.toLocaleString("th-TH")} รายการ</strong><span>Dispatch เป็น 1 Transfer</span></div><button type="button" className="primary" onClick={() => void submitDispatch()} disabled={busy || !canReviewSend || !online}>{busy ? "กำลังยืนยัน…" : "ยืนยันส่งโอน"}</button></>
              ) : (
                <><button type="button" className="secondary" onClick={onClose} disabled={busy}>ยกเลิก</button><div className="pos-stock-batch__footer-summary"><strong>{draft.length.toLocaleString("th-TH")} รายการ</strong><span>{destination ? destination.branch_name : "ยังไม่เลือกปลายทาง"}</span></div><button type="button" className="primary" onClick={() => { if (canReviewSend) { setError(null); setReviewing(true); } else setError("เลือกปลายทางและตรวจจำนวนทุกสินค้าก่อน"); }} disabled={!canReviewSend}>ตรวจสอบก่อนส่ง</button></>
              )}
            </footer>
          </>
        ) : (
          <>
            {!online ? <div className="pos-stock-batch__offline">ออฟไลน์ · เปิดคิวรับและตรวจจำนวนจาก Local Projection ได้ แต่ยังยืนยันรับไม่ได้</div> : null}
            <main className="pos-stock-transfer__receive-body">
              {receiveTransfer ? (
                <section className="pos-stock-transfer__receive-detail">
                  <button type="button" className="pos-stock-transfer__back" onClick={() => { setReceiveTransfer(null); setReviewing(false); setError(null); }} disabled={busy}>‹ กลับคิวรับ</button>
                  <div className="pos-stock-transfer__receive-heading"><span>ส่งจาก {receiveTransfer.source_branch_name}</span><strong>{transferTime(receiveTransfer.dispatched_at)}</strong></div>
                  <div className="pos-stock-transfer__receive-lines">
                    {receiveDraft.map((line) => {
                      const received = Number(line.received);
                      const invalid = !line.received.trim() || !Number.isFinite(received) || received < 0 || received > line.dispatched;
                      const short = !invalid && received < line.dispatched;
                      return (
                        <article key={line.stockItemId} data-short={short ? "true" : "false"}>
                          <div><strong>{line.name}</strong><span>ส่งมา {quantityLabel(line.dispatched)} {line.unitName}</span></div>
                          <label><span>รับจริง</span><input type="number" min="0" max={line.dispatched} step="any" inputMode="decimal" value={line.received} onFocus={(event) => event.currentTarget.select()} onChange={(event) => changeReceiveQuantity(line.stockItemId, event.target.value)} disabled={reviewing || busy} aria-invalid={invalid} /></label>
                          {short ? <em>ขาด {quantityLabel(line.dispatched - received)} {line.unitName}</em> : null}
                        </article>
                      );
                    })}
                  </div>
                  <label className="pos-stock-batch__note"><span>รายละเอียด{receiveVariance ? " *" : " (ถ้ามี)"}</span><textarea value={note} onChange={(event) => { setNote(event.target.value); changed(); }} rows={2} maxLength={2000} placeholder={receiveVariance ? "ระบุสาเหตุที่รับไม่ครบ / เสียหาย" : "หมายเหตุการรับโอน"} disabled={reviewing || busy} /></label>
                  {receiveVariance ? <div className="pos-stock-transfer__variance">มีส่วนต่าง · ระบบจะรับเข้าตามจำนวนจริงและส่งส่วนต่างเข้า Review โดยไม่สร้าง Waste อัตโนมัติ</div> : null}
                  {error ? <div className="pos-stock-batch__error">{error}</div> : null}
                </section>
              ) : (
                <section className="pos-stock-transfer__queue">
                  <div className="pos-stock-transfer__queue-heading"><div><span>รอรับเข้า</span><strong>{inbound.length.toLocaleString("th-TH")}</strong></div><small>อ่านจาก Local Projection</small></div>
                  {!inbound.length ? <div className="pos-stock-batch__empty"><strong>ไม่มีรายการระหว่างทางที่รอรับ</strong></div> : inbound.map((transfer) => (
                    <button type="button" key={transfer.transfer_id} onClick={() => openReceive(transfer)}>
                      <span><strong>{transfer.source_branch_name}</strong><small>{transfer.lines.length.toLocaleString("th-TH")} รายการ · ส่ง {transferTime(transfer.dispatched_at)}</small></span><em>ตรวจรับ ›</em>
                    </button>
                  ))}
                  {reviewRequired.length ? (
                    <div className="pos-stock-transfer__review-list"><h3>มีส่วนต่างรอตรวจสอบ</h3>{reviewRequired.map((transfer) => <article key={transfer.transfer_id}><strong>{transfer.source_branch_name} → {transfer.destination_branch_name}</strong><span>{transfer.lines.filter((line) => Number(line.variance_quantity ?? 0) > 0).map((line) => `${line.canonical_name} ขาด ${quantityLabel(Number(line.variance_quantity ?? 0))}`).join(" · ")}</span></article>)}</div>
                  ) : null}
                </section>
              )}
            </main>
            {receiveTransfer ? (
              <footer className="pos-stock-batch__footer">
                {reviewing ? <><button type="button" className="secondary" onClick={() => { setReviewing(false); setError(null); }} disabled={busy}>กลับไปแก้</button><div className="pos-stock-batch__footer-summary"><strong>{receiveDraft.length.toLocaleString("th-TH")} รายการ</strong><span>{receiveVariance ? "มีส่วนต่าง" : "รับครบ"}</span></div><button type="button" className="primary" onClick={() => void submitReceive()} disabled={busy || !online || !canReviewReceive}>{busy ? "กำลังยืนยัน…" : "ยืนยันรับจริง"}</button></> : <><button type="button" className="secondary" onClick={() => setReceiveTransfer(null)} disabled={busy}>กลับ</button><div className="pos-stock-batch__footer-summary"><strong>{receiveDraft.length.toLocaleString("th-TH")} รายการ</strong><span>{receiveInvalidCount ? `ต้องตรวจ ${receiveInvalidCount}` : receiveVariance ? "มีส่วนต่าง" : "จำนวนครบ"}</span></div><button type="button" className="primary" onClick={() => { if (canReviewReceive) { setError(null); setReviewing(true); } else setError(receiveVariance && !note.trim() ? "กรุณาระบุสาเหตุของส่วนต่างก่อน" : "ตรวจจำนวนรับจริงทุกสินค้าก่อน"); }} disabled={!canReviewReceive}>ตรวจสอบก่อนรับ</button></>}
              </footer>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
